import Papa from 'papaparse';
import {
  ScenarioNode,
  stripDcDgMarker
} from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanningUtils';

export type CsvGranularity = 'channel' | 'platform' | 'tactic';
export type CsvInputType = 'raw' | 'percent';

export interface ParsedUploadRow {
  key: string;
  newSpend: number;
}

export interface CsvIssue {
  type: 'error' | 'warning';
  message: string;
  row?: number;
}

const MIN_SPEND_FACTOR = 0.5;
const MAX_SPEND_FACTOR = 2.0;
const LARGE_CHANGE_THRESHOLD = 0.75;

const normalizeName = (s: string): string => stripDcDgMarker(s).trim().toLowerCase();

const GRANULARITY_COLUMNS: Record<CsvGranularity, string[]> = {
  channel: ['channel'],
  platform: ['channel', 'platform'],
  tactic: ['channel', 'platform', 'tactic']
};

const GRANULARITY_DEPTH: Record<CsvGranularity, number> = {
  channel: 0,
  platform: 1,
  tactic: 2
};

export const collectNodesAtLevel = (
  tree: ScenarioNode[],
  granularity: CsvGranularity
): { key: string; node: ScenarioNode }[] => {
  const targetDepth = GRANULARITY_DEPTH[granularity];

  const collect = (
    nodes: ScenarioNode[],
    depth: number,
    prefix: string
  ): { key: string; node: ScenarioNode }[] =>
    nodes.flatMap(node => {
      const displayName = stripDcDgMarker(node.name);
      const key = prefix ? `${prefix}, ${displayName}` : displayName;

      if (depth === targetDepth) {
        return node.isDemandCapture ? [] : [{ key, node }];
      }
      return node.children?.length ? collect(node.children, depth + 1, key) : [];
    });

  return collect(tree, 0, '');
};

export const generateTemplate = (
  tree: ScenarioNode[],
  granularity: CsvGranularity,
  inputType: CsvInputType
): Record<string, any>[] => {
  const colNames = GRANULARITY_COLUMNS[granularity];
  return collectNodesAtLevel(tree, granularity).map(({ key, node }) => {
    const parts = key.split(', ');
    const row: Record<string, any> = {};
    colNames.forEach((col, i) => {
      row[col] = parts[i] ?? '';
    });
    row.baseline_spend = Math.round(node.baselineDirectSpend);
    if (inputType === 'raw') {
      row.new_spend = Math.round(node.baselineDirectSpend);
    } else {
      row.percent_change = 0;
    }
    return row;
  });
};

export const parseCsvUpload = (
  csvText: string,
  tree: ScenarioNode[],
  granularity: CsvGranularity,
  inputType: CsvInputType
): { rows: ParsedUploadRow[]; issues: CsvIssue[] } => {
  const issues: CsvIssue[] = [];
  const colNames = GRANULARITY_COLUMNS[granularity];
  const valueColumn = inputType === 'raw' ? 'new_spend' : 'percent_change';
  const requiredColumns = [...colNames, 'baseline_spend', valueColumn];

  const parseResult = Papa.parse<Record<string, string>>(csvText, {
    header: true,
    skipEmptyLines: true
  });

  const headers = parseResult.meta.fields ?? [];
  const missingColumns = requiredColumns.filter(col => !headers.includes(col));
  if (missingColumns.length > 0) {
    issues.push({
      type: 'error',
      message: `Missing required columns: ${missingColumns.join(', ')}`
    });
    return { rows: [], issues };
  }

  const nodeByNormKey = new Map<string, { key: string; node: ScenarioNode }>();
  for (const entry of collectNodesAtLevel(tree, granularity)) {
    const normKey = entry.key.split(', ').map(normalizeName).join(', ');
    nodeByNormKey.set(normKey, entry);
  }

  const seenKeys = new Set<string>();
  const rows: ParsedUploadRow[] = [];
  let hasErrors = false;

  for (let i = 0; i < parseResult.data.length; i++) {
    const csvRow = parseResult.data[i];
    const rowNum = i + 1;

    const nameParts = colNames.map(col => (csvRow[col] ?? '').trim());
    const emptyIdx = nameParts.findIndex(p => !p);
    if (emptyIdx !== -1) {
      issues.push({
        type: 'error',
        message: `Row ${rowNum}: missing value for column "${colNames[emptyIdx]}"`,
        row: rowNum
      });
      hasErrors = true;
      continue;
    }

    const normKey = nameParts.map(p => normalizeName(p)).join(', ');
    const entry = nodeByNormKey.get(normKey);
    if (!entry) {
      issues.push({
        type: 'error',
        message: `Row ${rowNum}: "${nameParts.join(' > ')}" not found in scenario`,
        row: rowNum
      });
      hasErrors = true;
      continue;
    }

    if (seenKeys.has(normKey)) {
      issues.push({
        type: 'error',
        message: `Row ${rowNum}: duplicate entry for "${nameParts.join(' > ')}"`,
        row: rowNum
      });
      hasErrors = true;
      continue;
    }
    seenKeys.add(normKey);

    const rawValue = (csvRow[valueColumn] ?? '').trim();
    const numValue = parseFloat(rawValue);
    if (!rawValue || Number.isNaN(numValue)) {
      issues.push({
        type: 'error',
        message: `Row ${rowNum}: "${valueColumn}" must be a number (got "${rawValue}")`,
        row: rowNum
      });
      hasErrors = true;
      continue;
    }

    const baseline = entry.node.baselineDirectSpend;
    let newSpend: number;

    if (inputType === 'raw') {
      if (numValue < 0) {
        issues.push({
          type: 'error',
          message: `Row ${rowNum}: new_spend cannot be negative`,
          row: rowNum
        });
        hasErrors = true;
        continue;
      }
      newSpend = numValue;
    } else {
      if (numValue < -100) {
        issues.push({
          type: 'error',
          message: `Row ${rowNum}: percent_change of ${numValue}% would result in negative spend`,
          row: rowNum
        });
        hasErrors = true;
        continue;
      }
      newSpend = baseline * (1 + numValue / 100);
    }

    if (newSpend < MIN_SPEND_FACTOR * baseline - 0.99) {
      issues.push({
        type: 'error',
        message: `Row ${rowNum}: "${nameParts.join(' > ')}" cannot decrease by more than 50% from baseline`,
        row: rowNum
      });
      hasErrors = true;
      continue;
    }

    if (newSpend > MAX_SPEND_FACTOR * baseline + 0.99) {
      issues.push({
        type: 'error',
        message: `Row ${rowNum}: "${nameParts.join(' > ')}" cannot increase by more than 100% from baseline`,
        row: rowNum
      });
      hasErrors = true;
      continue;
    }

    if (baseline > 0 && Math.abs(newSpend - baseline) / baseline > LARGE_CHANGE_THRESHOLD) {
      issues.push({
        type: 'warning',
        message: `Row ${rowNum}: "${nameParts.join(' > ')}" has a large spend change — please verify`,
        row: rowNum
      });
    }

    rows.push({ key: entry.key, newSpend: Math.round(newSpend) });
  }

  return hasErrors ? { rows: [], issues } : { rows, issues };
};
